import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { RefreshCw, ExternalLink, RotateCcw, Tag, Loader2, Upload, UserCheck, Send, XCircle, CheckCircle2, Star, Archive, Reply, ChevronDown, ChevronRight, Search, Bell } from 'lucide-react'
import { format } from 'date-fns'
import { supabase } from '@/integrations/supabase/client'
import { toast } from '@/hooks/useToastHistory'

interface EmailReplyDetailsDialogProps {
  reply: any | null
  isOpen: boolean
  onClose: () => void
  onRefresh?: () => void
  onViewInSmartlead?: (reply: any) => void
  onManualFetchReplyCount?: (reply: any) => void
  onCategorize?: (replyId: string) => void
  onManualSmartleadSync?: (replyId: string) => void
  onTrackSDRResponse?: (replyId: string) => void
  onIgnoreReply?: (replyId: string) => void
  onSendTestNotification?: (replyId: string, notificationType: 'positive' | 'negative') => Promise<void>
  onSendManualNotification?: (replyId: string, notificationType: string) => Promise<void>
  categorizingIds?: Set<string>
  trackingSDRIds?: Set<string>
  ignoringIds?: Set<string>
  sendingTestNotificationId?: string | null
  fetchingReplyCount?: boolean
}

export function EmailReplyDetailsDialog({
  reply,
  isOpen,
  onClose,
  onRefresh,
  onViewInSmartlead,
  onManualFetchReplyCount,
  onCategorize,
  onManualSmartleadSync,
  onTrackSDRResponse,
  onIgnoreReply,
  onSendTestNotification,
  onSendManualNotification,
  categorizingIds = new Set(),
  trackingSDRIds = new Set(),
  ignoringIds = new Set(),
  sendingTestNotificationId = null,
  fetchingReplyCount = false
}: EmailReplyDetailsDialogProps) {
  // Gmail-style features state
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set(['sent', 'reply']))
  const [starredMessages, setStarredMessages] = useState<Set<string>>(new Set())
  const [archivedMessages, setArchivedMessages] = useState<Set<string>>(new Set())
  const [threadSearch, setThreadSearch] = useState('')
  
  // Slack Notifications state
  const [slackNotifications, setSlackNotifications] = useState<any[]>([])
  const [loadingNotifications, setLoadingNotifications] = useState(false)
  const [sendingManualNotification, setSendingManualNotification] = useState(false)
  const [previousRepliesPositive, setPreviousRepliesPositive] = useState<boolean | null>(null)
  const [notificationSentAt, setNotificationSentAt] = useState<string | null>(null)

  // Helper functions
  const formatRelativeTime = (dateString: string) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
    
    if (diffInHours < 1) return 'Just now'
    if (diffInHours < 24) return `${diffInHours}h ago`
    if (diffInHours < 48) return 'Yesterday'
    return format(date, 'MMM d')
  }

  // Fetch Slack notifications for this reply
  const fetchSlackNotifications = async () => {
    if (!reply || !reply.id || !reply.client_id) return
    
    setLoadingNotifications(true)
    try {
      // Determine expected notification type based on reply category and count
      let expectedMessageTypes: string[] = []
      
      if (reply.reply_count > 2) {
        // Follow-up replies (reply_count > 2) - check if previous replies were positive
        // We'll check this separately, but include followupReplyNotification in the search
        expectedMessageTypes = ['followupReplyNotification']
      } else if (reply.reply_category_type === 'positive') {
        if (reply.reply_count === 1) {
          expectedMessageTypes = ['positiveReplyNotification']
        } else if (reply.reply_count === 2) {
          expectedMessageTypes = ['secondPositiveReplyNotification']
        }
      } else if (reply.reply_category_type === 'negative' || reply.reply_category_type === 'neutral') {
        expectedMessageTypes = ['negativeReplyNotification']
      }
      
      // For follow-up replies, only search if we know previous replies were positive
      if (reply.reply_count > 2 && previousRepliesPositive === false) {
        setSlackNotifications([])
        setNotificationSentAt(null)
        return
      }
      
      if (expectedMessageTypes.length === 0 && reply.reply_count <= 2) {
        setSlackNotifications([])
        setNotificationSentAt(null)
        return
      }
      
      // Get the categorized_at timestamp to find notifications sent around that time
      // Since there's no direct reply_id link, we'll show all notifications of the expected type
      // within a reasonable time window, prioritizing those closest to the reply timestamp
      const categorizedAt = reply.categorized_at || reply.created_at
      const replyCreatedAt = reply.created_at || reply.event_timestamp
      
      // Use the earlier of categorized_at or created_at as the base time
      // For follow-up replies, use created_at since there's no categorized_at
      const baseTime = categorizedAt || replyCreatedAt
      
      // For replies, look at a wider window: from 24 hours before to 7 days after
      // This accounts for batch processing, delays, and the fact that notifications
      // might be sent at different times than categorization
      // For follow-up replies, also look at recent notifications (last hour) in case it was just sent
      let query = supabase
        .from('slack_message_logs')
        .select('*')
        .eq('client_id', reply.client_id)
        .in('message_type', expectedMessageTypes)
        .order('sent_at', { ascending: false })
        .limit(50) // Get more notifications to find potential matches
      
      if (baseTime) {
        // For follow-up replies, also include very recent notifications (last hour) in case it was just sent
        if (reply.reply_count > 2) {
          // Look from 1 hour before to 7 days after the reply creation
          const startTime = new Date(new Date(baseTime).getTime() - 60 * 60 * 1000).toISOString()
          const endTime = new Date(new Date(baseTime).getTime() + 7 * 24 * 60 * 60 * 1000).toISOString()
          query = query.gte('sent_at', startTime).lte('sent_at', endTime)
        } else {
          // For categorized replies, look from 24 hours before to 7 days after
          const startTime = new Date(new Date(baseTime).getTime() - 24 * 60 * 60 * 1000).toISOString()
          const endTime = new Date(new Date(baseTime).getTime() + 7 * 24 * 60 * 60 * 1000).toISOString()
          query = query.gte('sent_at', startTime).lte('sent_at', endTime)
        }
      } else {
        // If no timestamp, look at last 14 days
        const fourteenDaysAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString()
        query = query.gte('sent_at', fourteenDaysAgo)
      }
      
      const { data, error } = await query
      
      if (error) {
        console.error('Error fetching Slack notifications:', error)
        setSlackNotifications([])
      } else {
        let filteredData = data || []
        
        // If we have a categorization timestamp, prioritize notifications sent after it
        // and sort by proximity to the categorization time
        if (categorizedAt && filteredData.length > 0 && reply.reply_count <= 2) {
          // For categorized replies (1st and 2nd), sort by proximity to categorization time
          filteredData.sort((a, b) => {
            const timeA = Math.abs(new Date(a.sent_at).getTime() - new Date(categorizedAt).getTime())
            const timeB = Math.abs(new Date(b.sent_at).getTime() - new Date(categorizedAt).getTime())
            return timeA - timeB
          })
          
          // Show the closest notification(s) - if there are multiple within 2 hours, show all
          // Otherwise, show the closest one
          const closestTime = Math.abs(new Date(filteredData[0].sent_at).getTime() - new Date(categorizedAt).getTime())
          const twoHours = 2 * 60 * 60 * 1000
          
          if (closestTime <= twoHours) {
            // Show all notifications within 2 hours of categorization
            filteredData = filteredData.filter(n => 
              Math.abs(new Date(n.sent_at).getTime() - new Date(categorizedAt).getTime()) <= twoHours
            )
          } else {
            // Show only the closest notification if it's more than 2 hours away
            // (likely not related, but show it anyway for transparency)
            filteredData = [filteredData[0]]
          }
        } else if (reply.reply_count > 2 && filteredData.length > 0) {
          // For follow-up replies, sort by most recent first (already sorted by sent_at DESC, but ensure it)
          filteredData.sort((a, b) => {
            return new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime()
          })
          // For follow-up replies, show the most recent notification
          // (they're already sorted by sent_at DESC from the query, so first one is most recent)
        }
        
        setSlackNotifications(filteredData)
        
        // For follow-up replies or when categorized_at is not available, use the most recent notification timestamp
        // Also check if we have a notification that matches this reply (by checking client_id and message_type)
        if (filteredData.length > 0) {
          // For follow-up replies, use the most recent notification timestamp
          if (reply.reply_count > 2 || !reply.categorized_at) {
            // Get the most recent notification (first in sorted array after sorting)
            const mostRecent = filteredData[0]
            if (mostRecent.sent_at) {
              setNotificationSentAt(mostRecent.sent_at)
            } else {
              setNotificationSentAt(null)
            }
          } else {
            // For categorized replies, use categorized_at if available, otherwise use notification timestamp
            if (reply.categorized_at) {
              setNotificationSentAt(null) // Will use categorized_at in display
            } else if (filteredData[0]?.sent_at) {
              setNotificationSentAt(filteredData[0].sent_at)
            } else {
              setNotificationSentAt(null)
            }
          }
        } else {
          setNotificationSentAt(null)
        }
      }
    } catch (err) {
      console.error('Error fetching Slack notifications:', err)
      setSlackNotifications([])
      setNotificationSentAt(null)
    } finally {
      setLoadingNotifications(false)
    }
  }
  
  // Check if previous replies were positive (for follow-up notifications)
  const checkPreviousReplies = async () => {
    if (!reply || !reply.to_email || !reply.client_id || reply.reply_count <= 2) {
      setPreviousRepliesPositive(null)
      return
    }
    
    try {
      // Get previous replies for this lead to check if any were positive
      const { data: previousReplies, error } = await supabase
        .from('obsync_dynamic_reports')
        .select('reply_category_type, reply_count')
        .eq('client_id', reply.client_id)
        .eq('event_type', 'EMAIL_REPLY')
        .or(`to_email.eq.${reply.to_email},from_email.eq.${reply.to_email}`)
        .lt('reply_count', reply.reply_count)
        .order('reply_count', { ascending: true })
      
      if (error) {
        console.error('Error checking previous replies:', error)
        setPreviousRepliesPositive(null)
        return
      }
      
      // Check if any previous reply was positive
      const hasPositiveReply = previousReplies?.some(r => r.reply_category_type === 'positive') || false
      setPreviousRepliesPositive(hasPositiveReply)
    } catch (err) {
      console.error('Error checking previous replies:', err)
      setPreviousRepliesPositive(null)
    }
  }
  
  // Send manual notification
  const handleSendManualNotification = async () => {
    if (!reply || !reply.id || !reply.client_id) return
    
    setSendingManualNotification(true)
    try {
      // Determine notification type
      let notificationType = 'followupReplyNotification'
      let channelType = 'positive_replies'
      
      if (reply.reply_count === 1) {
        notificationType = reply.reply_category_type === 'positive' 
          ? 'positiveReplyNotification' 
          : 'negativeReplyNotification'
        channelType = reply.reply_category_type === 'positive' 
          ? 'positive_replies' 
          : 'neutral_negative_replies'
      } else if (reply.reply_count === 2) {
        notificationType = reply.reply_category_type === 'positive' 
          ? 'secondPositiveReplyNotification' 
          : 'negativeReplyNotification'
        channelType = reply.reply_category_type === 'positive' 
          ? 'positive_replies' 
          : 'neutral_negative_replies'
      } else if (reply.reply_count > 2) {
        // For follow-up, use the already-checked previousRepliesPositive state
        if (previousRepliesPositive === null) {
          // If not checked yet, check now
          await checkPreviousReplies()
          // Re-check after async call
          const { data: previousReplies } = await supabase
            .from('obsync_dynamic_reports')
            .select('reply_category_type')
            .eq('client_id', reply.client_id)
            .eq('event_type', 'EMAIL_REPLY')
            .or(`to_email.eq.${reply.to_email},from_email.eq.${reply.to_email}`)
            .lt('reply_count', reply.reply_count)
          
          const hasPositive = previousReplies?.some(r => r.reply_category_type === 'positive') || false
          
          if (hasPositive) {
            notificationType = 'followupReplyNotification'
            channelType = 'positive_replies'
          } else {
            toast({
              title: 'Cannot Send Follow-up Notification',
              description: 'Follow-up notifications are only sent for leads who previously sent positive replies.',
              variant: 'destructive'
            })
            setSendingManualNotification(false)
            return
          }
        } else if (previousRepliesPositive) {
          notificationType = 'followupReplyNotification'
          channelType = 'positive_replies'
        } else {
          toast({
            title: 'Cannot Send Follow-up Notification',
            description: 'Follow-up notifications are only sent for leads who previously sent positive replies.',
            variant: 'destructive'
          })
          setSendingManualNotification(false)
          return
        }
      }
      
      // Call the manual notification handler if provided, otherwise call directly via Supabase function
      if (onSendManualNotification) {
        await onSendManualNotification(reply.id, notificationType)
      } else {
        // Call scheduled-slack-reports via Supabase function invoke
        const { data, error } = await supabase.functions.invoke('scheduled-slack-reports', {
          body: {
            reportType: notificationType,
            clientId: reply.client_id.toString(),
            channelType: channelType,
            replyId: reply.id,
            dryRun: false
          }
        })
        
        if (error) {
          console.error('Error sending notification:', error)
          toast({
            title: 'Failed to Send Notification',
            description: error.message || 'An error occurred while sending the notification.',
            variant: 'destructive'
          })
        } else if (data?.success) {
          // Update slack_notification_sent flag
          await supabase
            .from('obsync_dynamic_reports')
            .update({ slack_notification_sent: true })
            .eq('id', reply.id)
          
          // Refresh data
          if (onRefresh) onRefresh()
          
          // Wait a moment for the notification log to be written to the database
          // Then refresh notifications to get the timestamp
          setTimeout(async () => {
            await fetchSlackNotifications()
          }, 1500)
          
          // Also try to fetch immediately in case it's already there
          await fetchSlackNotifications()
          
          // Show success message
          toast({
            title: 'Notification Sent',
            description: 'The Slack notification has been sent successfully.',
          })
        } else {
          toast({
            title: 'Notification Failed',
            description: 'The notification failed to send. Please check the logs.',
            variant: 'destructive'
          })
        }
      }
    } catch (err) {
      console.error('Error sending manual notification:', err)
      alert('Failed to send notification. Please try again.')
    } finally {
      setSendingManualNotification(false)
    }
  }
  
  // Fetch notifications when reply changes
  useEffect(() => {
    if (reply && isOpen) {
      fetchSlackNotifications()
      if (reply.reply_count > 2) {
        checkPreviousReplies()
      }
    }
  }, [reply?.id, isOpen])
  
  const formatTimeToRespond = (seconds: number) => {
    if (!seconds || seconds === 0) return 'N/A'
    
    const days = Math.floor(seconds / 86400)
    const hours = Math.floor((seconds % 86400) / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    
    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ${hours} hour${hours !== 1 ? 's' : ''}`
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`
    } else {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`
    }
  }

  const toggleMessageExpansion = (messageId: string) => {
    const newExpanded = new Set(expandedMessages)
    if (newExpanded.has(messageId)) {
      newExpanded.delete(messageId)
    } else {
      newExpanded.add(messageId)
    }
    setExpandedMessages(newExpanded)
  }

  const toggleStar = (messageId: string) => {
    const newStarred = new Set(starredMessages)
    if (newStarred.has(messageId)) {
      newStarred.delete(messageId)
    } else {
      newStarred.add(messageId)
    }
    setStarredMessages(newStarred)
  }

  const toggleArchive = (messageId: string) => {
    const newArchived = new Set(archivedMessages)
    if (newArchived.has(messageId)) {
      newArchived.delete(messageId)
    } else {
      newArchived.add(messageId)
    }
    setArchivedMessages(newArchived)
  }

  const cleanHtmlContent = (html: string): string => {
    if (!html) return ''
    
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html
    let text = tempDiv.textContent || tempDiv.innerText || ''
    
    // Handle forwarded message formatting
    text = text.replace(/---------- Forwarded message ---------/g, '\n\n---------- Forwarded message ---------\n')
    text = text.replace(/From: /g, '\nFrom: ')
    text = text.replace(/Date: /g, '\nDate: ')
    text = text.replace(/Subject: /g, '\nSubject: ')
    text = text.replace(/To: /g, '\nTo: ')
    
    // Handle common email separators
    text = text.replace(/On .* wrote:/g, '\n\n$&')
    text = text.replace(/El .* escribió:/g, '\n\n$&')
    
    // Handle common sentence endings and add line breaks
    text = text.replace(/\. /g, '.\n')
    text = text.replace(/\? /g, '?\n')
    text = text.replace(/! /g, '!\n')
    
    // Handle common email patterns
    text = text.replace(/Best regards,/g, '\n\nBest regards,')
    text = text.replace(/Kind regards,/g, '\n\nKind regards,')
    text = text.replace(/Sincerely,/g, '\n\nSincerely,')
    text = text.replace(/Thanks,/g, '\n\nThanks,')
    text = text.replace(/Thank you,/g, '\n\nThank you,')
    
    // Handle multilingual patterns
    text = text.replace(/Gesunde Grüße/g, '\n\nGesunde Grüße')
    text = text.replace(/Mit freundlichen Grüßen/g, '\n\nMit freundlichen Grüßen')
    
    // Preserve line breaks and improve formatting
    text = text.replace(/\r\n/g, '\n')
    text = text.replace(/\r/g, '\n')
    
    // Clean up multiple spaces but preserve intentional line breaks
    text = text.replace(/[ \t]+/g, ' ')
    
    // Add paragraph breaks for better readability
    text = text.replace(/\n\s*\n/g, '\n\n')
    
    // Clean up leading/trailing whitespace
    text = text.trim()
    
    return text
  }

  if (!reply) return null

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl w-[95vw] h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle>Email Reply Details</DialogTitle>
              <DialogDescription>
                Complete information for this auto reply
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              {/* Debug Refresh Button */}
              {onRefresh && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRefresh}
                  className="mr-2"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Data
                </Button>
              )}
              
              {/* View in Smartlead Button */}
              {onViewInSmartlead && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onViewInSmartlead(reply)}
                  className="mr-2"
                  disabled={!reply?.app_url}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  {reply?.app_url ? 'View in Smartlead' : 'No Smartlead URL'}
                </Button>
              )}
               
              {/* Manual Fetch Message History Button */}
              {onManualFetchReplyCount && reply?.campaign_id && reply?.sl_email_lead_id && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onManualFetchReplyCount(reply)}
                  disabled={fetchingReplyCount}
                  className="mr-2"
                >
                  <RotateCcw className={`w-4 h-4 mr-2 ${fetchingReplyCount ? 'animate-spin' : ''}`} />
                  {fetchingReplyCount ? 'Fetching...' : 'Fetch Message History'}
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>
        
        <div className="flex flex-col flex-1 min-h-0">
          <Tabs defaultValue="overview" className="flex flex-col flex-1">
            <TabsList className="grid w-full grid-cols-3 flex-shrink-0">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="messages">Messages</TabsTrigger>
              <TabsTrigger value="other">Other</TabsTrigger>
            </TabsList>
            
            <div className="flex-1 min-h-0 mt-4">
              <ScrollArea className="h-[calc(90vh-200px)] w-full">
                <div className="w-full pr-4">
                  <TabsContent value="overview" className="space-y-6 w-full">
                    {/* Lead Profile */}
                    <div className="bg-white border rounded-lg p-6">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                          {reply.to_email?.charAt(0).toUpperCase() || 'L'}
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold">
                            {reply.to_name || reply.to_email?.split('@')[0] || 'Unknown Lead'}
                          </h3>
                          <p className="text-gray-600">{reply.to_email}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              reply.response_category === 'Interested' ? 'bg-green-100 text-green-800' :
                              reply.response_category === 'Not Interested' ? 'bg-red-100 text-red-800' :
                              reply.response_category === 'Out Of Office' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {reply.response_category || 'Uncategorized'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Lead Timeline */}
                      <div className="space-y-4">
                        <h4 className="font-semibold text-gray-900">Lead Timeline</h4>
                        <div className="space-y-3">
                          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <div className="flex-1">
                              <div className="font-medium text-sm">Initial Outreach</div>
                              <div className="text-xs text-gray-500">
                                {reply.campaign_name || 'Unknown Campaign'}
                                {reply.campaign_id && (
                                  <span className="ml-2 text-blue-600">(ID: {reply.campaign_id})</span>
                                )}
                              </div>
                            </div>
                            <div className="text-xs text-gray-500">
                              {reply.created_at ? new Date(reply.created_at).toLocaleDateString() : 'N/A'}
                            </div>
                          </div>

                          <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <div className="flex-1">
                              <div className="font-medium text-sm">Reply Received</div>
                              <div className="text-xs text-gray-500">
                                {reply.response_category || 'Uncategorized'}
                              </div>
                            </div>
                            <div className="text-xs text-gray-500">
                              {reply.event_timestamp ? new Date(reply.event_timestamp).toLocaleDateString() : 'N/A'}
                            </div>
                          </div>

                          {reply.response_category === 'Meeting Request' || reply.response_category === 'Meeting booked' ? (
                            <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                              <div className="flex-1">
                                <div className="font-medium text-sm">Meeting Scheduled</div>
                                <div className="text-xs text-gray-500">Lead expressed interest in meeting</div>
                              </div>
                              <div className="text-xs text-gray-500">Pending</div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    {/* Reply Categorization */}
                    <div className="bg-white border rounded-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-4">Reply Categorization</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Response Category:</span>
                          <span className="font-medium">{reply.response_category || 'Uncategorized'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Category ID:</span>
                          <span className="font-medium">{reply.response_category_id || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Category Type:</span>
                          <span className="font-medium">{reply.reply_category_type || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">AI Model Used:</span>
                          <span className="font-medium">{reply.ai_model_used || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Categorized At:</span>
                          <span className="font-medium">
                            {reply.categorized_at ? new Date(reply.categorized_at).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Smartlead Sync:</span>
                          <span className={`font-medium ${
                            reply.smartlead_sync_status === 'success' ? 'text-green-600' :
                            reply.smartlead_sync_status === 'failed' || reply.smartlead_sync_status === 'error' ? 'text-red-600' :
                            reply.smartlead_sync_status === 'skipped' ? 'text-yellow-600' :
                            'text-gray-500'
                          }`}>
                            {reply.smartlead_sync_status === 'success' ? '✅ Synced' :
                             reply.smartlead_sync_status === 'failed' ? '❌ Failed' :
                             reply.smartlead_sync_status === 'error' ? '❌ Error' :
                             reply.smartlead_sync_status === 'skipped' ? '⏭️ Skipped (2nd+ reply)' :
                             reply.smartlead_sync_status === 'not_applicable' ? '— Not Applicable' :
                             '— Not Synced'}
                          </span>
                        </div>
                        {reply.smartlead_sync_error && (
                          <div className="flex flex-col gap-1 pt-2 border-t">
                            <span className="text-gray-600 text-sm">Sync Error:</span>
                            <span className="text-red-600 text-xs font-mono bg-red-50 p-2 rounded break-all">
                              {reply.smartlead_sync_error}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between pt-2 border-t">
                          <span className="text-gray-600">Smartlead Ignored:</span>
                          <span className={`font-medium ${
                            reply.smartlead_ignore_status === 'success' ? 'text-green-600' :
                            reply.smartlead_ignore_status === 'failed' || reply.smartlead_ignore_status === 'error' ? 'text-red-600' :
                            'text-gray-500'
                          }`}>
                            {reply.smartlead_ignore_status === 'success' ? '✅ Ignored in Stats' :
                             reply.smartlead_ignore_status === 'failed' ? '❌ Failed' :
                             reply.smartlead_ignore_status === 'error' ? '❌ Error' :
                             reply.smartlead_ignore_status === 'not_applicable' ? '— Not Applicable' :
                             '— Not Ignored'}
                            {reply.smartlead_ignore_retry_count > 0 && (
                              <span className="text-xs text-gray-500 ml-2">
                                (Retries: {reply.smartlead_ignore_retry_count})
                              </span>
                            )}
                          </span>
                        </div>
                        {reply.smartlead_ignore_error && (
                          <div className="flex flex-col gap-1 pt-2">
                            <span className="text-gray-600 text-sm">Ignore Error:</span>
                            <span className="text-red-600 text-xs font-mono bg-red-50 p-2 rounded break-all">
                              {reply.smartlead_ignore_error}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Campaign Context */}
                    <div className="bg-white border rounded-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-4">Campaign Context</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Campaign:</span>
                          <span className="font-medium">{reply.campaign_name || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Campaign ID:</span>
                          <span className="font-medium">{reply.campaign_id || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Sequence:</span>
                          <span className="font-medium">{reply.sequence_number || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Lead ID:</span>
                          <span className="font-medium">{reply.sl_email_lead_id || 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                    {/* SDR Response Tracking - Only show for positive replies */}
                    {reply.reply_category_type === 'positive' && (
                      <div className="bg-white border rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-semibold text-gray-900">SDR Response Tracking</h4>
                          <UserCheck className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Last Checked:</span>
                            <span className="font-medium">
                              {reply.last_sdr_poll_at 
                                ? new Date(reply.last_sdr_poll_at).toLocaleString('en-GB', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    timeZone: 'Europe/London'
                                  })
                                : 'Never checked'}
                            </span>
                          </div>
                          
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">SDR Response Status:</span>
                            <span className={`font-medium px-2 py-1 rounded-full text-xs ${
                              reply.sdr_response_tracking_status === 'responded' 
                                ? 'bg-green-100 text-green-800'
                                : reply.sdr_response_tracking_status === 'no_response'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : reply.sdr_response_tracking_status === 'waiting'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-gray-100 text-gray-800'
                            }`}>
                              {reply.sdr_response_tracking_status 
                                ? reply.sdr_response_tracking_status.charAt(0).toUpperCase() + 
                                  reply.sdr_response_tracking_status.slice(1).replace('_', ' ')
                                : 'Not tracked'}
                            </span>
                          </div>

                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Has SDR Replied:</span>
                            <span className={`font-medium px-2 py-1 rounded-full text-xs ${
                              reply.lead_without_a_reply === false
                                ? 'bg-green-100 text-green-800'
                                : reply.lead_without_a_reply === true
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-800'
                            }`}>
                              {reply.lead_without_a_reply === false
                                ? 'Yes ✓'
                                : reply.lead_without_a_reply === true
                                  ? 'No ✗'
                                  : 'Unknown'}
                            </span>
                          </div>

                          {reply.average_time_to_respond && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Average Time to Respond:</span>
                              <span className="font-medium">
                                {formatTimeToRespond(reply.average_time_to_respond)}
                              </span>
                            </div>
                          )}

                          {reply.first_sdr_response_timestamp && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">First SDR Response:</span>
                              <span className="font-medium">
                                {new Date(reply.first_sdr_response_timestamp).toLocaleString('en-GB', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  timeZone: 'Europe/London'
                                })}
                              </span>
                            </div>
                          )}

                          {reply.first_positive_reply_timestamp && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Positive Reply Received:</span>
                              <span className="font-medium">
                                {new Date(reply.first_positive_reply_timestamp).toLocaleString('en-GB', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  timeZone: 'Europe/London'
                                })}
                              </span>
                            </div>
                          )}

                          {reply.average_time_to_respond && reply.first_positive_reply_timestamp && (
                            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <div className="text-xs text-blue-800">
                                <strong>Response Time Analysis:</strong> SDR responded{' '}
                                {formatTimeToRespond(reply.average_time_to_respond)} after the positive reply was received.
                              </div>
                            </div>
                          )}

                          {!reply.last_sdr_poll_at && (
                            <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                              <div className="text-xs text-yellow-800">
                                <strong>Note:</strong> SDR tracking has not been performed yet. Click "Track SDR Response" to check the current status.
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Slack Notifications Section */}
                    <div className="bg-white border rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-gray-900">Slack Notifications</h4>
                        <Bell className="w-5 h-5 text-purple-600" />
                      </div>
                      {loadingNotifications ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="w-4 h-4 animate-spin text-gray-400 mr-2" />
                          <span className="text-sm text-gray-600">Loading notifications...</span>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {/* Primary notification status from slack_notification_sent flag */}
                          {/* Show notification status for categorized replies OR follow-up replies (reply_count > 2) */}
                          {(reply.reply_category_type || (reply.reply_count > 2 && previousRepliesPositive !== null)) ? (
                            <div className="border rounded-lg p-3 bg-gray-50">
                              <div className="flex justify-between items-start mb-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-medium text-gray-900">
                                      {reply.reply_count === 1 && reply.reply_category_type === 'positive' && '1st Positive Reply'}
                                      {reply.reply_count === 2 && reply.reply_category_type === 'positive' && '2nd Positive Reply'}
                                      {reply.reply_count > 2 && 'Follow-up Reply'}
                                      {(reply.reply_category_type === 'negative' || reply.reply_category_type === 'neutral') && 'Negative/Neutral Reply'}
                                      {!reply.reply_category_type && reply.reply_count > 2 && 'Follow-up Reply (No categorization required)'}
                                    </span>
                                    {reply.slack_notification_sent ? (
                                      <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                                        Sent
                                      </span>
                                    ) : (
                                      <span className="px-2 py-0.5 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">
                                        Not Sent
                                      </span>
                                    )}
                                  </div>
                                  {reply.slack_notification_sent && (
                                    <div className="text-sm text-gray-600">
                                      {reply.categorized_at ? (
                                        <>
                                          Sent after categorization: {new Date(reply.categorized_at).toLocaleString('en-GB', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            timeZone: 'Europe/London'
                                          })}
                                        </>
                                      ) : notificationSentAt ? (
                                        <>
                                          Sent: {new Date(notificationSentAt).toLocaleString('en-GB', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            timeZone: 'Europe/London'
                                          })}
                                        </>
                                      ) : (
                                        <>
                                          Sent: Just now
                                        </>
                                      )}
                                    </div>
                                  )}
                                  {reply.reply_count > 2 && !reply.reply_category_type && (
                                    <div className="text-sm text-gray-600 mt-1">
                                      {previousRepliesPositive 
                                        ? 'Previous replies were positive - follow-up notification eligible'
                                        : 'No previous positive replies found - follow-up notification not applicable'}
                                    </div>
                                  )}
                                  {reply.slack_notification_sent && slackNotifications.length > 0 && (
                                    <div className="mt-2 text-xs text-gray-500">
                                      Found {slackNotifications.length} matching notification(s) in logs
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Manual Send Button - Show if notification not sent and eligible */}
                              {!reply.slack_notification_sent && (
                                <div className="mt-3 pt-3 border-t">
                                  {(reply.reply_category_type || (reply.reply_count > 2 && previousRepliesPositive)) ? (
                                    <Button
                                      onClick={handleSendManualNotification}
                                      disabled={sendingManualNotification}
                                      size="sm"
                                      variant="outline"
                                      className="w-full"
                                    >
                                      {sendingManualNotification ? (
                                        <>
                                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                          Sending...
                                        </>
                                      ) : (
                                        <>
                                          <Send className="w-4 h-4 mr-2" />
                                          Send Notification Manually
                                        </>
                                      )}
                                    </Button>
                                  ) : reply.reply_count > 2 && !previousRepliesPositive ? (
                                    <div className="text-xs text-gray-500 text-center py-2">
                                      Follow-up notifications only sent for leads with previous positive replies
                                    </div>
                                  ) : null}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-center py-4 text-gray-500 text-sm">
                              {reply.reply_count > 2 
                                ? 'Checking previous replies...'
                                : 'Reply not yet categorized - notification will be sent after categorization'}
                            </div>
                          )}
                          
                          {/* Additional notifications from logs (if any) */}
                          {slackNotifications.length > 0 && (
                            <div className="mt-4">
                              <div className="text-xs font-medium text-gray-700 mb-2">Additional Notifications Found:</div>
                              {slackNotifications.map((notification, index) => (
                                <div key={notification.id || index} className="border rounded-lg p-3 bg-blue-50 mb-2">
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="text-sm font-medium text-gray-900">
                                          {notification.message_type === 'positiveReplyNotification' && '1st Positive Reply'}
                                          {notification.message_type === 'secondPositiveReplyNotification' && '2nd Positive Reply'}
                                          {notification.message_type === 'followupReplyNotification' && 'Follow-up Reply'}
                                          {notification.message_type === 'negativeReplyNotification' && 'Negative/Neutral Reply'}
                                        </span>
                                        {notification.success ? (
                                          <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                                            Sent
                                          </span>
                                        ) : (
                                          <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                                            Failed
                                          </span>
                                        )}
                                      </div>
                                      {notification.sent_at && (
                                        <div className="text-xs text-gray-600">
                                          {new Date(notification.sent_at).toLocaleString('en-GB', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            timeZone: 'Europe/London'
                                          })}
                                        </div>
                                      )}
                                      {notification.channel_type && (
                                        <div className="mt-1 text-xs text-gray-500">
                                          Channel: {notification.channel_type}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="messages" className="space-y-4 w-full h-full flex flex-col">
                    {/* Thread Context Banner */}
                    {reply?.thread_id && reply?.reply_sequence && (
                      <div className="flex-shrink-0 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                              📧
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-blue-900">
                                Email Thread Conversation
                              </div>
                              <div className="text-xs text-blue-700">
                                This is reply #{reply.reply_sequence} in a conversation thread
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                              Reply {reply.reply_sequence}
                            </span>
                            <span className="text-xs text-blue-600">
                              Thread: {reply.thread_id?.slice(0, 12)}...
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Gmail-style Email Thread */}
                    <div className="flex-1 min-h-0 bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col">
                      {/* Email Header */}
                      <div className="border-b border-gray-200 p-4 bg-gray-50 flex-shrink-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {reply?.subject || 'No subject'}
                            </h3>
                            {reply?.thread_id && reply?.reply_sequence && (
                              <div className="flex items-center gap-2">
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                                  Thread #{reply.reply_sequence}
                                </span>
                                {reply.reply_sequence > 1 && (
                                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                                    Follow-up
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          {reply?.thread_id && (
                            <div className="text-xs text-gray-500">
                              ID: {reply.thread_id?.slice(0, 12)}...
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                          <span>From: {reply?.from_email}</span>
                          <span>To: {reply?.to_email}</span>
                          <span>{formatRelativeTime(reply?.created_at || '')}</span>
                        </div>
                        
                        {/* Search Bar */}
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                          <Input
                            placeholder="Search in this thread..."
                            value={threadSearch}
                            onChange={(e) => setThreadSearch(e.target.value)}
                            className="pl-10 text-sm"
                          />
                        </div>
                      </div>

                      {/* Email Content */}
                      <div className="flex-1 overflow-y-auto">
                        {/* Sent Message */}
                        <div className="border-b border-gray-100 p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                              {reply?.from_email?.charAt(0).toUpperCase() || 'S'}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">Sent Message</span>
                                  <span className="text-xs text-gray-500">•</span>
                                  <span className="text-xs text-gray-500">Outbound</span>
                                  <span className="text-xs text-gray-500">•</span>
                                  <span className="text-xs text-gray-500">{formatRelativeTime(reply?.created_at || '')}</span>
                                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => toggleStar('sent')}
                                    className="h-6 w-6 p-0"
                                  >
                                    <Star className={`w-4 h-4 ${starredMessages.has('sent') ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400'}`} />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => toggleArchive('sent')}
                                    className="h-6 w-6 p-0"
                                  >
                                    <Archive className={`w-4 h-4 ${archivedMessages.has('sent') ? 'text-orange-500' : 'text-gray-400'}`} />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => toggleMessageExpansion('sent')}
                                    className="h-6 w-6 p-0"
                                  >
                                    {expandedMessages.has('sent') ? 
                                      <ChevronDown className="w-4 h-4 text-gray-400" /> : 
                                      <ChevronRight className="w-4 h-4 text-gray-400" />
                                    }
                                  </Button>
                                </div>
                              </div>
                              {expandedMessages.has('sent') && (
                                <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                                  {(() => {
                                    const sentMessageText = reply.sent_message?.text || 
                                                           reply.sent_message?.html || 
                                                           reply.sent_message_text || 
                                                           reply.sent_message_body || ''
                                    
                                    return cleanHtmlContent(sentMessageText) || 'No sent message available'
                                  })()}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Reply Message */}
                        <div className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                              {reply?.to_email?.charAt(0).toUpperCase() || 'R'}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">Reply</span>
                                  <span className="text-xs text-gray-500">•</span>
                                  <span className="text-xs text-gray-500">Inbound</span>
                                  <span className="text-xs text-gray-500">•</span>
                                  <span className="text-xs text-gray-500">{formatRelativeTime(reply?.created_at || '')}</span>
                                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => toggleStar('reply')}
                                    className="h-6 w-6 p-0"
                                  >
                                    <Star className={`w-4 h-4 ${starredMessages.has('reply') ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400'}`} />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => toggleArchive('reply')}
                                    className="h-6 w-6 p-0"
                                  >
                                    <Archive className={`w-4 h-4 ${archivedMessages.has('reply') ? 'text-orange-500' : 'text-gray-400'}`} />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0"
                                  >
                                    <Reply className="w-4 h-4 text-gray-400" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => toggleMessageExpansion('reply')}
                                    className="h-6 w-6 p-0"
                                  >
                                    {expandedMessages.has('reply') ? 
                                      <ChevronDown className="w-4 h-4 text-gray-400" /> : 
                                      <ChevronRight className="w-4 h-4 text-gray-400" />
                                    }
                                  </Button>
                                </div>
                              </div>
                              {expandedMessages.has('reply') && (
                                <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                                  {(() => {
                                    const rawReplyContent = reply.preview_text || reply.reply_message_text || reply.reply_body || ''
                                    const cleanedContent = cleanHtmlContent(rawReplyContent)
                                    return cleanedContent || 'No reply content available'
                                  })()}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Full Thread History */}
                        {reply.thread_id && (
                          <div className="border-t border-gray-100 p-4 bg-gray-50">
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                                <span className="text-xs">📧</span>
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-3">
                                  <span className="font-medium text-gray-900">Full Thread History</span>
                                  <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded-full">
                                    Reply {reply.reply_sequence} of {reply.reply_sequence}
                                  </span>
                                </div>
                                
                                {/* Thread Timeline */}
                                <div className="space-y-2">
                                  {Array.from({ length: reply.reply_sequence }, (_, i) => i + 1).map((seq) => (
                                    <div key={seq} className="flex items-center gap-2 text-sm">
                                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                                        seq === reply.reply_sequence 
                                          ? 'bg-blue-500 text-white' 
                                          : seq < reply.reply_sequence 
                                            ? 'bg-green-500 text-white' 
                                            : 'bg-gray-300 text-gray-600'
                                      }`}>
                                        {seq}
                                      </div>
                                      <span className={`text-sm ${
                                        seq === reply.reply_sequence 
                                          ? 'font-medium text-blue-700' 
                                          : 'text-gray-600'
                                      }`}>
                                        {seq === 1 ? 'Initial outreach' : 
                                         seq === reply.reply_sequence ? 'Current reply' : 
                                         `Reply ${seq}`}
                                      </span>
                                      {seq === reply.reply_sequence && (
                                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                                          You are here
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                
                                <div className="mt-3 text-xs text-gray-500">
                                  💡 <strong>Campaign Performance:</strong> Based on first positive reply<br/>
                                  💡 <strong>Lead Status:</strong> Based on latest reply sentiment
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* AI Categorization Footer */}
                      <div className="border-t border-gray-200 p-4 bg-gray-50 flex-shrink-0">
                        {reply?.response_category ? (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-2">
                                <Tag className="w-4 h-4 text-purple-600" />
                                <span className="text-sm font-medium text-gray-900">
                                  {reply.response_category}
                                </span>
                                {reply.ai_confidence && (
                                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                                    {Math.round(reply.ai_confidence * 100)}% confidence
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {onCategorize && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => onCategorize(reply.id)}
                                        disabled={categorizingIds.has(reply.id) || (reply.reply_count && reply.reply_count > 2)}
                                      >
                                        {categorizingIds.has(reply.id) ? (
                                          <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Re-categorizing...
                                          </>
                                        ) : (
                                          <>
                                            <Tag className="w-4 h-4 mr-2" />
                                            Re-categorize
                                          </>
                                        )}
                                      </Button>
                                    </TooltipTrigger>
                                    {reply.reply_count && reply.reply_count > 2 && (
                                      <TooltipContent>
                                        <p>Only replies with count 1 or 2 can be categorized</p>
                                      </TooltipContent>
                                    )}
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              
                              {/* Manual Smartlead Sync Button */}
                              {onManualSmartleadSync && reply.response_category && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => onManualSmartleadSync(reply.id)}
                                        disabled={
                                          categorizingIds.has(reply.id) ||
                                          !reply.campaign_id || 
                                          !reply.sl_email_lead_id ||
                                          !reply.response_category_id
                                        }
                                        className="border-blue-500 text-blue-600 hover:bg-blue-50"
                                      >
                                        {categorizingIds.has(reply.id) ? (
                                          <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Syncing...
                                          </>
                                        ) : (
                                          <>
                                            <Upload className="w-4 h-4 mr-2" />
                                            Sync to Smartlead
                                          </>
                                        )}
                                      </Button>
                                    </TooltipTrigger>
                                    {(!reply.campaign_id || !reply.sl_email_lead_id) && (
                                      <TooltipContent>
                                        <p>Missing campaign or lead data for sync</p>
                                      </TooltipContent>
                                    )}
                                    {!reply.response_category_id && (
                                      <TooltipContent>
                                        <p>Categorize reply first before syncing</p>
                                      </TooltipContent>
                                    )}
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              
                              {/* SDR Tracking Button */}
                              {onTrackSDRResponse && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => onTrackSDRResponse(reply.id)}
                                        disabled={
                                          trackingSDRIds.has(reply.id) || 
                                          reply.reply_category_type !== 'positive' ||
                                          !reply.campaign_id || 
                                          !reply.sl_email_lead_id
                                        }
                                      >
                                        {trackingSDRIds.has(reply.id) ? (
                                          <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Tracking...
                                          </>
                                        ) : (
                                          <>
                                            <UserCheck className="w-4 h-4 mr-2" />
                                            Track SDR Response
                                          </>
                                        )}
                                      </Button>
                                    </TooltipTrigger>
                                    {reply.reply_category_type !== 'positive' && (
                                      <TooltipContent>
                                        <p>SDR tracking is only available for positive replies</p>
                                      </TooltipContent>
                                    )}
                                    {reply.reply_category_type === 'positive' && (!reply.campaign_id || !reply.sl_email_lead_id) && (
                                      <TooltipContent>
                                        <p>Campaign ID or Lead ID is missing</p>
                                      </TooltipContent>
                                    )}
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              
                              {/* Ignore Reply Button */}
                              {onIgnoreReply && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => onIgnoreReply(reply.id)}
                                        disabled={
                                          ignoringIds.has(reply.id) ||
                                          !reply.stats_id ||
                                          reply.smartlead_ignore_status === 'success' ||
                                          !['Auto-Reply', 'Out Of Office', 'No Longer Works Here', 'Sender Originated Bounce'].includes(reply.response_category || '')
                                        }
                                        className={reply.smartlead_ignore_status === 'success' ? 'border-green-500 text-green-600' : 'border-orange-500 text-orange-600 hover:bg-orange-50'}
                                      >
                                        {ignoringIds.has(reply.id) ? (
                                          <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Ignoring...
                                          </>
                                        ) : reply.smartlead_ignore_status === 'success' ? (
                                          <>
                                            <CheckCircle2 className="w-4 h-4 mr-2" />
                                            Already Ignored
                                          </>
                                        ) : (
                                          <>
                                            <XCircle className="w-4 h-4 mr-2" />
                                            Ignore in Smartlead
                                          </>
                                        )}
                                      </Button>
                                    </TooltipTrigger>
                                    {!reply.stats_id && (
                                      <TooltipContent>
                                        <p>Missing Smartlead stats_id for this reply</p>
                                      </TooltipContent>
                                    )}
                                    {reply.smartlead_ignore_status === 'success' && (
                                      <TooltipContent>
                                        <p>This reply is already excluded from Smartlead stats</p>
                                      </TooltipContent>
                                    )}
                                    {reply.stats_id && !['Auto-Reply', 'Out Of Office', 'No Longer Works Here', 'Sender Originated Bounce'].includes(reply.response_category || '') && (
                                      <TooltipContent>
                                        <p>Only auto-reply types can be ignored. This is categorized as "{reply.response_category}".</p>
                                      </TooltipContent>
                                    )}
                                    {reply.stats_id && reply.smartlead_ignore_status !== 'success' && ['Auto-Reply', 'Out Of Office', 'No Longer Works Here', 'Sender Originated Bounce'].includes(reply.response_category || '') && (
                                      <TooltipContent>
                                        <p>Exclude this reply from Smartlead stats (for auto-replies, bounces, OOO)</p>
                                      </TooltipContent>
                                    )}
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              
                              {/* Test Notification Button */}
                              {onSendTestNotification && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={async () => {
                                          const notifType = reply.reply_category_type === 'positive' ? 'positive' : 'negative'
                                          await onSendTestNotification(reply.id, notifType)
                                        }}
                                        disabled={sendingTestNotificationId === reply.id}
                                        className="border-blue-500 text-blue-600 hover:bg-blue-50"
                                      >
                                        {sendingTestNotificationId === reply.id ? (
                                          <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Sending Test...
                                          </>
                                        ) : (
                                          <>
                                            <Send className="w-4 h-4 mr-2" />
                                            Send Test Notification
                                          </>
                                        )}
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Send test notification to Loop test channels</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Tag className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-600">
                                Not categorized yet
                              </span>
                            </div>
                            {onCategorize && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="sm"
                                      onClick={() => onCategorize(reply.id)}
                                      disabled={categorizingIds.has(reply.id) || (reply.reply_count && reply.reply_count > 2)}
                                    >
                                      {categorizingIds.has(reply.id) ? (
                                        <>
                                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                          Categorizing...
                                        </>
                                      ) : (
                                        <>
                                          <Tag className="w-4 h-4 mr-2" />
                                          Categorize
                                        </>
                                      )}
                                    </Button>
                                  </TooltipTrigger>
                                  {reply.reply_count && reply.reply_count > 2 && (
                                    <TooltipContent>
                                      <p>Only replies with count 1 or 2 can be categorized</p>
                                    </TooltipContent>
                                  )}
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        )}
                        {reply?.reasoning && (
                          <div className="mt-2 text-xs text-gray-600 bg-white p-2 rounded border">
                            <strong>AI Reasoning:</strong> {reply.reasoning}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Conversation History */}
                    {reply.thread_id && reply.reply_sequence > 1 && (
                      <div className="flex-shrink-0">
                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Conversation History</h4>
                          <div className="text-xs text-gray-600">
                            This is reply {reply.reply_sequence} in a conversation thread. 
                            Previous replies in this thread have been processed and categorized.
                          </div>
                          <div className="mt-2 text-xs text-gray-500">
                            💡 <strong>Campaign Performance:</strong> Based on first positive reply<br/>
                            💡 <strong>Lead Status:</strong> Based on latest reply sentiment
                          </div>
                        </div>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="other" className="space-y-6 w-full h-full flex flex-col">
                    <div className="flex flex-col h-full">
                      <h4 className="text-lg font-semibold mb-4 flex-shrink-0">Raw Details</h4>
                      <div className="p-4 bg-muted rounded-lg w-full flex-1 overflow-auto">
                        <pre className="text-xs whitespace-pre-wrap break-words">
                          {JSON.stringify(reply, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </TabsContent>
                </div>
              </ScrollArea>
            </div>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  )
}
